import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DEFAULT_LOOKUPS, round2 } from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { money } from '../../lib/format';
import { currencySymbol, isForeignCurrency, toBase } from '../../lib/currency';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/ui';
import { AccountPicker } from '../../components/RecordPicker';
import { FxRateField } from '../../components/FxRateField';
import { Attachments, PendingFiles, uploadAttachments } from '../../components/Attachments';

interface OpenInvoice {
  id: string;
  number: string;
  total: string;
  paidAmount: string;
  dueAt: string;
}

interface CashAccount {
  id: string;
  name: string;
  isDefault: boolean;
  currency?: string;
}

/**
 * تسجيل تحصيل من صفحة التحصيلات نفسها.
 *
 * كان التحصيل يُسجَّل من الفاتورة أو من ملف العميل وحدهما. لكن معظم تاريخ
 * العملاء دخل قيودًا من الجدول القديم بلا فواتير في النظام، فلا فاتورة
 * يُضغط فيها «تسجيل تحصيل». هنا يُختار العميل أولًا، ثم فاتورةٌ مفتوحة له
 * إن وُجدت — أو «تحت الحساب» فتُرحَّل الدفعة من ذمّته مباشرةً.
 *
 * والسند (إيصال، صورة تحويل) يُرفق مع الحفظ: الدفعة أولًا ثم ملفاتها
 * عليها، ففشل الرفع لا يُضيّع الدفعة — تبقى النافذة مفتوحة لإعادة الرفع.
 */
export function RecordCollectionModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  /** بعد الحفظ والرفع بلا خطأ — برقم الدفعة للتأكيد. */
  onDone: (number: string) => void;
}) {
  const [account, setAccount] = useState<{ id: string; name: string } | null>(null);
  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState(DEFAULT_LOOKUPS.paymentMethod[0]!);
  const [cashAccountId, setCashAccountId] = useState('');
  const [fxRate, setFxRate] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  /* دفعة حُفظت وتعثّر رفع سندها — تُعرض مرفقاتها لإعادة المحاولة. */
  const [saved, setSaved] = useState<{ id: string; number: string; error: string } | null>(null);

  /* الفواتير غير المسدَّدة للعميل المختار — المسودة والملغاة لا تقبل تحصيلًا. */
  const invoices = useQuery({
    queryKey: ['invoices', 'open-for', account?.id],
    queryFn: () =>
      api<{ items: OpenInvoice[] }>(
        `/finance/invoices${qs({ accountId: account!.id, unpaidOnly: true, pageSize: 100, sort: 'dueAt', order: 'asc' })}`,
      ),
    enabled: Boolean(account),
  });
  const openInvoices = invoices.data?.items ?? [];
  const invoice = openInvoices.find((i) => i.id === invoiceId) ?? null;
  const remaining = invoice ? round2(Number(invoice.total) - Number(invoice.paidAmount)) : null;

  /* خزائن النظام وحساباته البنكية — والافتراضية مختارة سلفًا. */
  const cashAccounts = useQuery({
    queryKey: ['cash-accounts', false],
    queryFn: () => api<{ items: CashAccount[] }>('/finance/cash-accounts'),
  });
  useEffect(() => {
    if (cashAccountId || !cashAccounts.data) return;
    const def = cashAccounts.data.items.find((c) => c.isDefault) ?? cashAccounts.data.items[0];
    if (def) setCashAccountId(def.id);
  }, [cashAccounts.data, cashAccountId]);

  const cashCurrency =
    cashAccounts.data?.items.find((c) => c.id === cashAccountId)?.currency ?? 'EGP';
  const foreign = isForeignCurrency(cashCurrency);
  const rate = Number(fxRate) || 0;
  const value = Number(amount) || 0;
  /* ما سيُخصم من الفاتورة بالجنيه — يُقارن بالمتبقي. */
  const baseValue = foreign ? (rate > 0 ? toBase(value, rate) : 0) : value;
  const exceeds = remaining !== null && baseValue > remaining + 0.01;

  const save = useMutation({
    mutationFn: async () => {
      const created = await api<{ id: string; number: string }>('/finance/payments', {
        method: 'POST',
        body: {
          accountId: account!.id,
          invoiceId: invoiceId || null,
          amount: foreign ? toBase(value, rate) : value,
          ...(foreign ? { fxAmount: value, fxRate: rate } : {}),
          paidAt: new Date(paidAt).toISOString(),
          method: method || undefined,
          reference: reference.trim() || undefined,
          notes: notes.trim() || undefined,
          cashAccountId: cashAccountId || undefined,
        },
      });
      const uploadError = files.length
        ? await uploadAttachments('payment', created.id, files)
        : null;
      return { ...created, uploadError };
    },
    onSuccess: (res) => {
      if (res.uploadError) {
        setSaved({ id: res.id, number: res.number, error: res.uploadError });
        return;
      }
      onDone(res.number);
    },
  });

  const valid =
    Boolean(account) && value > 0 && (!foreign || rate > 0) && !exceeds && Boolean(paidAt);

  /* حُفظت الدفعة وتعثّر سندها: لا رجوع إلى النموذج — إعادة الحفظ تُكرّر الدفعة. */
  if (saved) {
    return (
      <Modal
        title={`التحصيل ${saved.number}`}
        onClose={() => onDone(saved.number)}
        footer={
          <button className="btn btn-primary" onClick={() => onDone(saved.number)}>
            تم
          </button>
        }
      >
        <div className="alert alert-warning mb-4">
          <Icon name="alert" size={18} />
          <div>
            سُجّل التحصيل {saved.number}، لكن تعذّر رفع بعض الملفات: {saved.error} أعد رفعها من
            هنا.
          </div>
        </div>
        <Attachments entity="payment" entityId={saved.id} title="سند التحصيل" />
      </Modal>
    );
  }

  return (
    <Modal
      title="تسجيل تحصيل"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'سجّل التحصيل'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {!account ? (
        <AccountPicker
          label="العميل الذي دفع"
          onPick={(id, name) => {
            setAccount({ id, name });
            setInvoiceId('');
          }}
        />
      ) : (
        <>
          <div className="row mb-4" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
            <span className="tiny mute">العميل</span>
            <strong>{account.name}</strong>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => {
                setAccount(null);
                setInvoiceId('');
              }}
            >
              تغيير
            </button>
          </div>

          <div className="form-grid">
            <div className="field span-2">
              <label className="label" htmlFor="rc-invoice">
                الفاتورة
              </label>
              <select
                id="rc-invoice"
                className="select"
                value={invoiceId}
                onChange={(e) => setInvoiceId(e.target.value)}
                disabled={invoices.isPending}
              >
                <option value="">تحت الحساب / بدون فاتورة</option>
                {openInvoices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.number} — المتبقي {money(round2(Number(i.total) - Number(i.paidAmount)))}
                  </option>
                ))}
              </select>
              <span className="tiny mute">
                {invoices.isPending
                  ? 'جارٍ جلب فواتير العميل المفتوحة…'
                  : invoice
                    ? `المتبقي على الفاتورة ${money(remaining)}.`
                    : openInvoices.length
                      ? 'بلا فاتورة: تُخصم الدفعة من رصيد العميل مباشرةً.'
                      : 'لا فواتير مفتوحة لهذا العميل — تُسجَّل الدفعة على رصيده مباشرةً.'}
              </span>
            </div>

            <div className="field">
              <label className="label req" htmlFor="rc-amount">
                المبلغ{foreign ? ` (${currencySymbol(cashCurrency)})` : ''}
              </label>
              <input
                id="rc-amount"
                className="input num"
                type="number"
                min="0"
                step="any"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
              />
              {exceeds && (
                <span className="field-error">
                  المبلغ يتجاوز المتبقي على الفاتورة وقدره {money(remaining)}. سجّل الزيادة تحت
                  الحساب.
                </span>
              )}
            </div>

            <div className="field">
              <label className="label req" htmlFor="rc-date">
                تاريخ التحصيل
              </label>
              <input
                id="rc-date"
                className="input"
                type="date"
                value={paidAt}
                onChange={(e) => setPaidAt(e.target.value)}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="rc-cash">
                الخزينة / البنك
              </label>
              <select
                id="rc-cash"
                className="select"
                value={cashAccountId}
                onChange={(e) => setCashAccountId(e.target.value)}
              >
                {(cashAccounts.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {isForeignCurrency(c.currency) ? ` (${c.currency})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {foreign && (
              <FxRateField
                id="rc-rate"
                currency={cashCurrency}
                date={paidAt}
                rate={fxRate}
                onRate={setFxRate}
                fxTotal={value}
              />
            )}

            <div className="field">
              <label className="label" htmlFor="rc-method">
                طريقة الدفع
              </label>
              <select
                id="rc-method"
                className="select"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              >
                {DEFAULT_LOOKUPS.paymentMethod.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="label" htmlFor="rc-ref">
                المرجع
              </label>
              <input
                id="rc-ref"
                className="input"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="رقم الشيك أو التحويل"
                dir="ltr"
              />
            </div>

            <div className="field span-2">
              <label className="label" htmlFor="rc-notes">
                ملاحظات
              </label>
              <textarea
                id="rc-notes"
                className="textarea"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <PendingFiles
              id="rc-files"
              files={files}
              onChange={setFiles}
              label="سند التحصيل (اختياري)"
              hint="إيصال الاستلام أو صورة التحويل أو الشيك — صورة أو PDF، يُرفق على الدفعة."
            />
          </div>
        </>
      )}
    </Modal>
  );
}
